<template>
  <v-container class="white lighten-5">
    <v-row>
      <v-col>
        <h3 class="mb-4">DECLARED GOODS - Package Labeling</h3>
        
        <!-- Control Panel -->
        <v-card class="mb-4">
          <v-card-title>Labels</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedImage"
                  :items="imageOptions"
                  item-text="name"
                  item-value="value"
                  label="Füge ein Gefahren-Label hinzu"
                  prepend-icon="mdi-image"
                  @change="addImageLabel"
                >
                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <img :src="`/img/${item.value}`" class="preview-image mr-2" />
                      <span>{{ item.name }}</span>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <div class="d-flex align-center pa-2">
                      <img :src="`/img/${item.value}`" class="preview-image mr-3" />
                      <span>{{ item.name }}</span>
                    </div>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-textarea
                  v-model="textInput"
                  label="(Mehrzeiliger) Text"
                  prepend-icon="mdi-text"
                  append-outer-icon="mdi-plus"
                  @click:append-outer="addTextLabel"
                  rows="3"
                  auto-grow
                  hint="Drücke Ctrl+Enter oder +, um es hinzuzufügen"
                  @keydown="handleTextKeydown"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn color="error" @click="clearLabels" :disabled="labels.length === 0">
                  <v-icon left>mdi-delete</v-icon>
                  Alle Labels entfernen
                </v-btn>
                <v-btn color="primary" @click="submitPackage" :loading="submitting" class="ml-2">
                  <v-icon left>mdi-send</v-icon>
                  Senden
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 3D Package Canvas -->
        <v-card>
          <v-card-title>Package Visualization</v-card-title>
          <div class="canvas-container" ref="canvasContainer">
            <canvas 
              ref="packageCanvas" 
              class="package-canvas"
              @mousedown="startDragging"
              @mousemove="dragLabel"
              @mouseup="stopDragging"
              @mouseleave="stopDragging"
            />
            
            <!-- Draggable Labels -->
            <div
              v-for="(label, index) in labels"
              :key="`label-${index}`"
              :ref="`label-${index}`"
              class="draggable-label"
              :class="{ 'dragging': label.isDragging }"
              :style="getLabelStyle(label)"
              @mousedown="startLabelDrag(index, $event)"
            >
              <img v-if="label.type === 'image'" :src="`/img/${label.content}`" class="label-image" />
              <div v-else class="label-text">
                <div 
                  v-for="(line, lineIndex) in label.content.split('\n')" 
                  :key="`line-${lineIndex}`"
                  class="text-line"
                >
                  {{ line }}
                </div>
              </div>
              <v-btn 
                icon 
                x-small 
                class="delete-label" 
                @click="removeLabel(index)"
                color="error"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import DeclaredGoodsModel from "@/models/DeclaredGoodsModel";
import axios from 'axios';

export default {
  name: "DeclaredGoodsComponent",
  components: {},
  props: {
    item: {
      type: DeclaredGoodsModel
    },
  },
  data() {
    return {
      canvasWidth: 800,
      canvasHeight: 600,
      images: ["csm_Gefahrzettel_Klasse_1_982695d2c9.jpg", "csm_Gefahrzettel_Klasse_23_0d5bacbf63.jpg", "csm_Gefahrzettel_Klasse_21_203ee8a783.jpg"],
      selectedImage: null,
      textInput: '',
      labels: [],
      isDragging: false,
      dragIndex: -1,
      dragOffset: { x: 0, y: 0 },
      submitting: false
    }
  },
  computed: {
    canvas() {
      return this.$refs.packageCanvas;
    },
    ctx() {
      return this.canvas?.getContext('2d');
    },
    imageOptions() {
      return this.images.map((img, index) => ({
        name: `Hazard Label ${index + 1}`,
        value: img
      }));
    }
  },
  methods: {
    initCanvas() {
      if (!this.canvas) return;
      
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
      this.drawPackage();
    },
    
    drawPackage() {
      if (!this.ctx) return;
      
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      
      // Draw 3D cube package
      const cubeSize = 300;
      const cubeX = (this.canvasWidth - cubeSize) / 2;
      const cubeY = (this.canvasHeight - cubeSize) / 2;
      const depth = 80;
      
      // Front face
      ctx.fillStyle = '#E8E8E8';
      ctx.fillRect(cubeX, cubeY, cubeSize, cubeSize);
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.strokeRect(cubeX, cubeY, cubeSize, cubeSize);
      
      // Top face
      ctx.fillStyle = '#F5F5F5';
      ctx.beginPath();
      ctx.moveTo(cubeX, cubeY);
      ctx.lineTo(cubeX + depth, cubeY - depth);
      ctx.lineTo(cubeX + cubeSize + depth, cubeY - depth);
      ctx.lineTo(cubeX + cubeSize, cubeY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Right face
      ctx.fillStyle = '#D0D0D0';
      ctx.beginPath();
      ctx.moveTo(cubeX + cubeSize, cubeY);
      ctx.lineTo(cubeX + cubeSize + depth, cubeY - depth);
      ctx.lineTo(cubeX + cubeSize + depth, cubeY + cubeSize - depth);
      ctx.lineTo(cubeX + cubeSize, cubeY + cubeSize);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Add some depth lines
      ctx.beginPath();
      ctx.moveTo(cubeX, cubeY);
      ctx.lineTo(cubeX + depth, cubeY - depth);
      ctx.moveTo(cubeX, cubeY + cubeSize);
      ctx.lineTo(cubeX + depth, cubeY + cubeSize - depth);
      ctx.moveTo(cubeX + cubeSize, cubeY + cubeSize);
      ctx.lineTo(cubeX + cubeSize + depth, cubeY + cubeSize - depth);
      ctx.stroke();
    },
    
    addImageLabel() {
      if (!this.selectedImage) return;
      
      this.labels.push({
        type: 'image',
        content: this.selectedImage,
        x: 100,
        y: 100,
        width: 80,
        height: 80,
        isDragging: false
      });
      
      this.selectedImage = null;
    },
    
    addTextLabel() {
      if (!this.textInput.trim()) return;
      
      // Calculate dynamic height based on number of lines
      const lines = this.textInput.split('\n');
      const lineHeight = 20;
      const padding = 16;
      const calculatedHeight = Math.max(40, lines.length * lineHeight + padding);
      
      // Calculate dynamic width based on longest line
      const maxLineLength = Math.max(...lines.map(line => line.length));
      const calculatedWidth = Math.max(120, Math.min(300, maxLineLength * 8 + padding));
      
      this.labels.push({
        type: 'text', 
        content: this.textInput,
        x: 150,
        y: 150,
        width: calculatedWidth,
        height: calculatedHeight,
        isDragging: false
      });
      
      this.textInput = '';
    },
    
    handleTextKeydown(event) {
      // Add label on Ctrl+Enter
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        this.addTextLabel();
      }
    },
    
    getLabelStyle(label) {
      return {
        position: 'absolute',
        left: `${label.x}px`,
        top: `${label.y}px`,
        width: `${label.width}px`,
        height: `${label.height}px`,
        cursor: label.isDragging ? 'grabbing' : 'grab'
      };
    },
    
    startLabelDrag(index, event) {
      event.preventDefault();
      this.isDragging = true;
      this.dragIndex = index;
      
      const label = this.labels[index];
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      
      this.dragOffset = {
        x: event.clientX - rect.left - label.x,
        y: event.clientY - rect.top - label.y
      };
      
      this.$set(this.labels[index], 'isDragging', true);
    },
    
    startDragging(event) {
      // Handle canvas drag events if needed
    },
    
    dragLabel(event) {
      if (!this.isDragging || this.dragIndex === -1) return;
      
      event.preventDefault();
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      
      const newX = event.clientX - rect.left - this.dragOffset.x;
      const newY = event.clientY - rect.top - this.dragOffset.y;
      
      // Keep within canvas bounds
      const maxX = this.canvasWidth - this.labels[this.dragIndex].width;
      const maxY = this.canvasHeight - this.labels[this.dragIndex].height;
      
      this.$set(this.labels[this.dragIndex], 'x', Math.max(0, Math.min(newX, maxX)));
      this.$set(this.labels[this.dragIndex], 'y', Math.max(0, Math.min(newY, maxY)));
    },
    
    stopDragging() {
      if (this.isDragging && this.dragIndex !== -1) {
        this.$set(this.labels[this.dragIndex], 'isDragging', false);
      }
      this.isDragging = false;
      this.dragIndex = -1;
    },
    
    removeLabel(index) {
      this.labels.splice(index, 1);
    },
    
    clearLabels() {
      this.labels = [];
    },
    
    async capturePackageImage() {
      // Create a temporary canvas to combine the package and labels
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.canvasWidth;
      tempCanvas.height = this.canvasHeight;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Draw the package
      tempCtx.drawImage(this.canvas, 0, 0);
      
      // Draw labels on the canvas
      for (const label of this.labels) {
        if (label.type === 'image') {
          // Load and draw image
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = () => {
              tempCtx.drawImage(img, label.x, label.y, label.width, label.height);
              resolve();
            };
            img.src = `/img/${label.content}`;
          });
        } else {
          // Draw multiline text
          const lines = label.content.split('\n');
          const lineHeight = 20;
          const padding = 8;
          
          // Draw background
          tempCtx.fillStyle = '#fff';
          tempCtx.fillRect(label.x, label.y, label.width, label.height);
          tempCtx.strokeStyle = '#ddd';
          tempCtx.lineWidth = 1;
          tempCtx.strokeRect(label.x, label.y, label.width, label.height);
          
          // Draw text lines
          tempCtx.fillStyle = '#000';
          tempCtx.font = '14px Arial';
          tempCtx.textAlign = 'left';
          
          lines.forEach((line, index) => {
            const yPos = label.y + padding + (index + 1) * lineHeight;
            tempCtx.fillText(line, label.x + padding, yPos);
          });
        }
      }
      
      return tempCanvas.toDataURL('image/png');
    },
    
    async submitPackage() {
      this.submitting = true;
      
      try {
        const packageImage = await this.capturePackageImage();
        
        // Convert data URL to blob
        const response = await fetch(packageImage);
        const blob = await response.blob();
        
        // Create FormData
        const formData = new FormData();
        formData.append('packageImage', blob, 'labeled-package.png');
        formData.append('labels', JSON.stringify(this.labels));
        formData.append('timestamp', new Date().toISOString());
        
        // Submit to external API
        const apiResponse = await axios.post('/api/declared-goods/submit-package', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.$emit('package-submitted', apiResponse.data);
        alert('Package submitted successfully!');
        
      } catch (error) {
        console.error('Submission failed:', error);
        alert('Failed to submit package. Please try again.');
      } finally {
        this.submitting = false;
      }
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.initCanvas();
    });
  }
}
</script>

<style scoped>
.canvas-container {
  position: relative;
  display: inline-block;
  border: 2px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.package-canvas {
  display: block;
  background: #f9f9f9;
}

.draggable-label {
  position: absolute;
  border: 2px dashed transparent;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  user-select: none;
}

.draggable-label:hover {
  border-color: #2196F3;
}

.draggable-label.dragging {
  border-color: #FF9800;
  transform: scale(1.05);
  z-index: 1000;
}

.label-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.label-text {
  font-size: 14px;
  font-weight: bold;
  text-align: left;
  padding: 8px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.text-line {
  line-height: 1.4;
  margin-bottom: 2px;
}

.text-line:last-child {
  margin-bottom: 0;
}

.preview-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.delete-label {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #f44336 !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.delete-label:hover {
  background: #d32f2f !important;
}
</style>
